import { Search } from 'lucide-react'
import { useAtomValue } from 'jotai'
import dayjs from 'dayjs'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { fetchRobots } from '@/api/robots.api'
import { currentUserAtom } from '@/store/auth.atom'
import { fetchUnits } from '@/api/units.api'

export interface ActivityFilterValues {
  unitId: string | undefined
  unitName?: string
  robotId: string | undefined
  robotName?: string
  time: DateRangeValue | undefined
}

interface ActivityFilterPanelProps {
  values: ActivityFilterValues
  onChange: (values: ActivityFilterValues) => void
  onSearch: () => void
}

export function ActivityFilterPanel({
  values,
  onChange,
  onSearch,
}: ActivityFilterPanelProps) {
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  function set<TKey extends keyof ActivityFilterValues>(
    key: TKey,
    value: ActivityFilterValues[TKey],
  ) {
    onChange({ ...values, [key]: value })
  }

  const defaultDateRange: DateRangeValue = {
    from: dayjs().startOf('month').toDate(),
    to: dayjs().endOf('day').millisecond(0).toDate(),
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-300 bg-white px-6 py-5"
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[180px] flex-1 flex-col gap-1">
          <span className="text-sm font-medium">Robot</span>
          <Select
            cacheKey={`robot-select-activity-filter-${values.unitId ?? 'all'}`}
            initialPage={0}
            lazy
            placeholder="Tất cả robot"
            allowClear
            value={values.robotId ?? undefined}
            className="w-full"
            enableRemoteSearch
            allLabel="Tất cả robot"
            initialOptions={
              values.robotId && values.robotName
                ? [{ value: values.robotId, label: values.robotName }]
                : []
            }
            loadOptions={async ({ page, signal, searchText }) => {
              const res = await fetchRobots(
                page,
                10,
                {
                  robotId: searchText?.trim() || undefined,
                  unitId: values.unitId,
                },
                signal,
              )
              const arr = Array.isArray(res.items) ? res.items : []
              const pageSize = 10
              const hasNextPage = arr.length >= pageSize

              return {
                options: arr.map((u: any) => ({
                  value: u.id,
                  label: u.name,
                })),
                hasNextPage,
                nextPage: hasNextPage ? page + 1 : undefined,
              }
            }}
            onLazyValueChange={(robotId, option) => {
              onChange({
                ...values,
                robotId: robotId,
                robotName: option?.label ? String(option.label) : undefined,
              })
            }}
          />
        </div>

        {showUnitColumn && (
          <div className="flex min-w-[180px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Đơn vị</span>
            <Select
              cacheKey="activity-filter-unit-select"
              initialPage={0}
              lazy
              placeholder="Tất cả đơn vị"
              allowClear
              value={values.unitId ?? undefined}
              className="w-full"
              enableRemoteSearch
              allLabel="Tất cả đơn vị"
              initialOptions={
                values.unitId && values.unitName
                  ? [{ value: values.unitId, label: values.unitName }]
                  : []
              }
              loadOptions={async ({ page, signal, searchText }) => {
                const res = await fetchUnits(
                  page,
                  10,
                  { keySearch: searchText?.trim() || undefined },
                  signal,
                )
                const arr = Array.isArray(res.items) ? res.items : []
                const pageSize = 10
                const hasNextPage = arr.length >= pageSize

                return {
                  options: arr.map((u: any) => ({
                    value: u.id,
                    label: u.name,
                  })),
                  hasNextPage,
                  nextPage: hasNextPage ? page + 1 : undefined,
                }
              }}
              onLazyValueChange={(unitId, option) => {
                onChange({
                  ...values,
                  unitId: unitId,
                  unitName: option?.label ? String(option.label) : undefined,
                  robotId: undefined,
                  robotName: undefined,
                })
              }}
            />
          </div>
        )}

        <div className="flex min-w-[280px] flex-1 flex-col gap-1">
          <span className="text-sm font-medium">Thời gian</span>
          <DateRangePicker
            value={values.time ?? defaultDateRange}
            onChange={(v) => set('time', v)}
            className="w-full"
          />
        </div>

        <Button
          variant="outline"
          size="md"
          onClick={onSearch}
          className="gap-2 rounded-full border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10 px-6 shrink-0"
        >
          <Search size={16} />
          Tìm kiếm
        </Button>
      </div>
    </FilterFormWrapper>
  )
}
